import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  Clock,
  MapPin,
  Recycle,
  CheckCircle2,
  ArrowRight,
  Tag,
  Layers,
  ScrollText,
  Scale,
  Briefcase,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Feucherolles ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de vos fournitures de déménagement {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la gestion du temps est la clé d&apos;un
              changement de domicile réussi, notre service logistique
              prend en charge l&apos;intégralité du transport de vos
              équipements directement chez vous.
            </p>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Cette réactivité à l&apos;échelle locale vous permet
              d&apos;aborder vos préparatifs l&apos;esprit léger. En
              éliminant la corvée du transport des fournitures, vous
              gagnez de précieuses heures pour trier vos affaires et
              pouvez commencer à concevoir vos colis dès
              l&apos;arrivée du livreur, en toute sérénité.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Des engagements concrets",
                body: "Nous vous garantissons une livraison rapide à plat, avec des emballages impeccablement propres et secs, directement sur le pas de votre porte à Feucherolles ainsi que dans les communes voisines (Saint-Nom-la-Bretèche, Chavenay, Davron, Crespières).",
                Icon: MapPin,
              },
              {
                title: "Un confort total",
                body: "Vous planifiez la réception selon vos disponibilités, vous évitant ainsi les trajets fatigants dans les zones commerciales des Yvelines et l'encombrement de votre véhicule personnel.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Reconditionnés −40% (hero card sombre) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Reconditionnés −40 %
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos cartons reconditionnés : la qualité pro à moins de
            40&nbsp;% du prix du neuf
          </h2>
        </div>

        {/* Card sombre éco-malin */}
        <div className="mt-8 relative overflow-hidden rounded-3xl bg-[#0f1d2b] text-white p-8 md:p-12">
          {/* Badge orange */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF5722] text-white text-[11px] uppercase tracking-[0.18em] font-extrabold">
            <Recycle className="h-3.5 w-3.5" strokeWidth={2.8} />
            Éco-malin · une seule utilisation
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <p className="text-[15px] md:text-[16px] text-white/85 leading-relaxed">
                Pour concilier économies et haute résistance, nous
                avons développé une gamme exclusive de cartons
                reconditionnés méticuleusement sélectionnés par nos
                équipes. Ces emballages proviennent de circuits
                professionnels spécifiques et n&apos;ont servi
                qu&apos;une unique fois, ce qui leur permet de
                conserver toute leur rigidité structurelle
                d&apos;origine sans subir le moindre affaissement
                ou déchirure.
              </p>
              <p className="mt-4 text-[14.5px] text-white/75 leading-relaxed">
                Ce choix astucieux vous permet de réduire
                considérablement le budget global de vos fournitures
                sans jamais transiger sur la sécurité de vos biens.
                C&apos;est la solution parfaite pour emballer vos
                pièces volumineuses, vos vêtements ou votre linge de
                maison dans des contenants solides, vous garantissant
                ainsi une démarche à la fois économique et
                écoresponsable pour votre installation {city.aCity}.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/cartons-reconditionnes"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#FF5722] hover:bg-[#e84a17] text-white font-bold text-[14px] transition-colors"
                  data-testid="city-feucherolles-cta-reconditionnes"
                >
                  <Recycle className="h-4 w-4" strokeWidth={2.6} />
                  Voir les cartons reconditionnés
                  <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                </Link>
                <Link
                  to="/kits-cartons"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-[14px] transition-colors"
                  data-testid="city-feucherolles-cta-kits-eco"
                >
                  <Boxes className="h-4 w-4" strokeWidth={2.6} />
                  Découvrir les kits
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#FF5722]">
                  Remise
                </div>
                <div className="mt-2 font-display font-extrabold text-[44px] leading-none text-white">
                  −40 %
                </div>
                <div className="mt-2 text-[12.5px] text-white/70 leading-snug">
                  vs cartons neufs
                </div>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#237cb5]">
                  Utilisation
                </div>
                <div className="mt-2 font-display font-extrabold text-[44px] leading-none text-white">
                  1
                </div>
                <div className="mt-2 text-[12.5px] text-white/70 leading-snug">
                  seul transit pro
                </div>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 sm:col-span-2">
                <div className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#237cb5]">
                  Contrôle qualité
                </div>
                <div className="mt-2 text-[14px] text-white/85 leading-relaxed">
                  Tri manuel, vérification de la rigidité et
                  élimination de toute boîte présentant le moindre
                  signe de faiblesse.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Conseils déménagement organisé ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode &amp; organisation
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos conseils pour un déménagement organisé
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un matériel de qualité supérieure n&apos;est pleinement
            efficace que s&apos;il est associé à une méthode de
            préparation rigoureuse et bien structurée au quotidien.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            {
              tag: "MARQUAGE",
              title: "Le marquage systématique de vos colis",
              body: "Pensez à noter sur chaque carton son contenu exact ainsi que sa pièce de destination finale sur le dessus et sur au moins un côté afin de faciliter grandement le travail de manutention et le dispatching le jour J.",
              Icon: ScrollText,
              accent: "#237cb5",
            },
            {
              tag: "RÉPARTITION",
              title: "La répartition stratégique des charges",
              body: "Placez toujours les objets les plus denses ou lourds comme les livres et la vaisselle au fond des cartons, puis complétez les espaces vides restants avec notre papier bulle ou du linge de maison pour empêcher tout mouvement dangereux durant le transport.",
              Icon: Scale,
              accent: "#237cb5",
            },
            {
              tag: "ANTICIPATION",
              title: "L'anticipation de la trousse de secours",
              body: "Préparez un carton \"prioritaire\" ou un sac dédié contenant l'essentiel pour vos premières 24 heures (documents administratifs, chargeurs, trousse de toilette, quelques outils de base et de la vaisselle jetable) afin de ne pas avoir à ouvrir dix boîtes dès votre première nuit.",
              Icon: Briefcase,
              accent: "#FF5722",
            },
            {
              tag: "SCELLAGE",
              title: "La sécurisation des fonds de cartons",
              body: "Renforcez systématiquement la fermeture de vos boîtes en appliquant une bande de ruban adhésif professionnel en croix sur le dessous, ce qui consolide la structure et élimine définitivement le risque de voir le carton céder sous le poids lors du levage.",
              Icon: Tag,
              accent: "#FF5722",
            },
          ].map(({ tag, title, body, Icon, accent }) => (
            <div
              key={tag}
              className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 overflow-hidden"
            >
              <span
                className="absolute top-0 left-0 h-1.5 w-full"
                style={{ backgroundColor: accent }}
              />
              <div className="flex items-center gap-3">
                <span
                  className="flex items-center justify-center h-11 w-11 rounded-xl text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <div
                    className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold"
                    style={{ color: accent }}
                  >
                    {tag}
                  </div>
                  <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-feucherolles-cta-fermeture"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs, dévidoirs &amp; marqueurs
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-feucherolles-cta-bulles"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Papier bulle &amp; calage
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-feucherolles-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
